import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminTable from '../../shared/AdminTable';
import WebhookEventDetailModal from './WebhookEventDetailModal';
import { PaddleWebhookEvent, AdminTableColumn, AdminTableAction } from '../../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface WebhookEventsTableProps {
  events: PaddleWebhookEvent[];
  loading?: boolean;
}

export default function WebhookEventsTable({ events, loading }: WebhookEventsTableProps) {
  const [selectedEvent, setSelectedEvent] = useState<PaddleWebhookEvent | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const columns: AdminTableColumn<PaddleWebhookEvent>[] = [
    {
      key: 'event_type',
      label: 'Event Type',
      sortable: true,
      width: 200,
      render: (row) => (
        <Chip label={row.event_type} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      key: 'paddle_subscription_id',
      label: 'Subscription ID',
      sortable: false,
      width: 200,
      render: (row) => (
        <Box sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
          {row.paddle_subscription_id}
        </Box>
      ),
    },
    {
      key: 'processing_status',
      label: 'Status',
      sortable: true,
      width: 120,
      render: (row) => (
        <Chip
          label={row.processing_status.toUpperCase()}
          size="small"
          color={row.processing_status === 'success' ? 'success' : 'error'}
        />
      ),
    },
    {
      key: 'retry_count',
      label: 'Retries',
      sortable: true,
      width: 100,
      render: (row) => row.retry_count,
    },
    {
      key: 'received_at',
      label: 'Received',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.received_at), { addSuffix: true }),
    },
    {
      key: 'error_message',
      label: 'Error',
      sortable: false,
      render: (row) =>
        row.error_message ? (
          <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.error_message}
          </Box>
        ) : (
          '-'
        ),
    },
  ];

  const actions: AdminTableAction<PaddleWebhookEvent>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => {
        setSelectedEvent(row);
        setDetailModalOpen(true);
      },
      color: 'primary',
    },
  ];

  return (
    <>
      <AdminTable columns={columns} data={events} actions={actions} isLoading={loading} />

      {selectedEvent && (
        <WebhookEventDetailModal
          open={detailModalOpen}
          event={selectedEvent}
          onClose={() => setDetailModalOpen(false)}
        />
      )}
    </>
  );
}
